<script setup lang="ts">
import { api } from '@/api';
import {
  AppPermissionsEnum,
  GetMedicalSystemTemplateRequest,
  MedicalSystemRtSystemDto,
} from '@/api/generator';
import PtwAddButton from '@/components/buttons/PtwAddButton.vue';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import { ContextMenuAction } from '@/components/ContextMenu/types';
import {
  getDefaultContextMenuActions,
  globalContextMenuHeaderItemDefinition,
} from '@/components/ContextMenu/utils';
import EntityChange from '@/components/EntityHistory/EntityChange.vue';
import FirstEntityFeedback from '@/components/PageFeedback/FirstEntityFeedback.vue';
import CommentCount from '@/components/forms/CommentCount/CommentCount.vue';
import { mainNavigationMedicalSystems } from '@/components/icons/icons';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import BaseImage from '@/components/images/BaseImage.vue';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import OverviewTableHeader from '@/components/OverviewTable/OverviewTableHeader.vue';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { routenames } from '@/router/routenames';
import { buildOdataQuery } from '@/utils/odata';
import type { I18nKey } from '@/utils/typings';
import { useLoading } from '@/utils/useLoading';
import { useToggle } from '@vueuse/core';
import { useTranslation } from 'i18next-vue';
import { mapKeys } from 'lodash';
import { computed, ref, watchEffect, useTemplateRef } from 'vue';
import { useRouter } from 'vue-router';
import MedicalSystemAddDialog from './components/MedicalSystemAddDialog.vue';
import MedicalSystemOverviewFilter from './components/MedicalSystemOverviewFilter.vue';
import type { MedicalSystemOverviewFilterObject } from './types';
import { useDeleteMedicalSystemWithConfirmation } from './utils';
import { EntityInfo } from '@/components/confirmationModals/types';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import { TAB_PREFIX } from '@/components/confirmationModals/useFormConfirmationModal';
import { getEntityChangeEvent } from '@/components/EntityHistory/entityChange';
import { useRemainingViewportHeight } from '@/utils/useRemainingVueport';
import { createOverviewImageProps } from '@/components/images/presets';
import { useTableScrollElement } from '@/utils/vuetifyMenuHelper';
import { localizedEnumCompare, cascadedCompareFunction } from '@/utils/compareAndSort';

const { t } = useTranslation();
const tableRef = useTemplateRef<HTMLDivElement>('table');
const { height } = useRemainingViewportHeight(tableRef);
const tableScrollElement = useTableScrollElement(tableRef);

const medicalSystems = ref<MedicalSystemRtSystemDto[]>();
const constMedicalSystems = ref<MedicalSystemRtSystemDto[]>();
const filterObject = ref<MedicalSystemOverviewFilterObject>({});

const router = useRouter();
const overviewImageProps = createOverviewImageProps({
  fallbackIcon: mainNavigationMedicalSystems,
  cover: true,
});

const {
  action: loadMedicalSystems,
  loading,
  loadingFirstTime,
} = useLoading(async () => {
  const { result } = await api.medicalSystemsApi.getMedicalSystemRtSystemList(
    buildOdataQuery({
      search: filterObject.value?.search,
      filter: {
        category: { in: filterObject.value?.category },
        subCategory: { in: filterObject.value?.subCategory },
      },
    }),
  );
  medicalSystems.value = result?.value || undefined;
  if (constMedicalSystems.value === undefined && medicalSystems.value?.length) {
    constMedicalSystems.value = [...medicalSystems.value];
  }
}, true);

async function add(selected: GetMedicalSystemTemplateRequest) {
  await router.push({
    name: routenames.medicalSystems.add,
    query: mapKeys(selected, (_value, key) => key),
  });
}

const { action: deleteAction } = useDeleteMedicalSystemWithConfirmation();

const headers = computed(() => [
  {
    title: '',
    key: 'image',
    sortable: false,
    width: overviewImageProps.desktopWidth,
  },
  {
    title: t('Name'),
    key: 'name',
    sortable: true,
  },
  {
    title: t('Description'),
    key: 'description',
    sortable: true,
    width: '20%',
  },
  {
    title: t('StateOfUse'),
    key: 'state',
    sort: localizedEnumCompare('StateOfUseEnum'),
  },
  {
    title: `${t('Category_one')} | '${t('Type_one')}`,
    key: 'category',
    sortable: true,
    sortRaw: cascadedCompareFunction<MedicalSystemRtSystemDto>([
      (a, b) => localizedEnumCompare('RtSystemCategoryEnum')(a.category, b.category),
      (a, b) => localizedEnumCompare('MedicalSystemSubCategoryEnum')(a.subCategory, b.subCategory),
    ]),
  },
  {
    title: `${t('Manufacturer')} | '${t('Model')}`,
    key: 'manufacturer',
    sortable: true,
    sortRaw: cascadedCompareFunction<MedicalSystemRtSystemDto>([
      (a, b) => localizedEnumCompare('RtSystemManufacturerEnum')(a.manufacturer, b.manufacturer),
      (a, b) => localizedEnumCompare('RtSystemModelEnum')(a.model, b.model),
    ]),
  },
  {
    title: t('LastModificationTime'),
    key: 'lastModificationTime',
    sortable: true,
  },
  globalContextMenuHeaderItemDefinition,
]);

function getContextMenuActions(item: MedicalSystemRtSystemDto): ContextMenuAction[] {
  const defaultContextMenuActions = getDefaultContextMenuActions(item);

  return [
    {
      ...defaultContextMenuActions.view,
      to: {
        name: routenames.medicalSystems.details,
        params: { id: item.id },
      },
    },
    {
      ...defaultContextMenuActions.edit,
      to: {
        name: routenames.medicalSystems.edit,
        params: { id: item.id },
      },
    },
    {
      ...defaultContextMenuActions.delete,
      callback: async () => {
        const { result } = await deleteAction({
          param: item.id,
          confirmationOptions: getDefaultDeleteConfirmationOptions(
            {
              ...(item as EntityInfo),
              typeName: 'MedicalSystem_one',
            },
            t,
          ),
        });
        if (result?.success) {
          constMedicalSystems.value = undefined;
          await loadMedicalSystems();
        }
      },
    },
  ];
}

function navigateTo(id: string, hash?: string) {
  router.push({
    name: routenames.medicalSystems.details,
    hash,
    params: { id },
  });
}

const [isAddDialogOpen, toggleAddDialog] = useToggle(false);

watchEffect(async () => {
  await loadMedicalSystems();
});

const subModelAvailable = computed(() =>
  medicalSystems.value?.some((medicalSystem) => !!medicalSystem?.subModel),
);

const showFirstEntityFeedback = computed(
  () =>
    !loading.value &&
    medicalSystems.value?.length === 0 &&
    Object.keys(filterObject.value).length === 0,
);
</script>

<template>
  <BasePage :loading="loadingFirstTime">
    <template #title>
      <BasePageTitle :title="$t('MedicalSystemEntity_other')" />
    </template>

    <template #actions>
      <PtwAddButton v-if="!showFirstEntityFeedback" @click="toggleAddDialog(true)" />
    </template>
    <FirstEntityFeedback
      v-if="showFirstEntityFeedback"
      :permission="AppPermissionsEnum.LocalMedicalSystemsAdd"
      :noPermissionMessage="$t('NoMedicalSystemsConfigured')"
      :icon="mainNavigationMedicalSystems"
      :actionText="$t('AddMedicalSystemEntity')"
      @action="toggleAddDialog(true)"
    />
    <template v-if="constMedicalSystems?.length">
      <MedicalSystemOverviewFilter v-model="filterObject" :items="constMedicalSystems" />
      <OverviewTable
        ref="table"
        class="mt-2"
        :headers="headers"
        itemLabel="MedicalSystemEntity"
        data-testid="DataTable_MedicalSystemEntity"
        :items="medicalSystems"
        :loading="loading"
        :height="height"
        fixedHeader
        :sortBy="[{ key: 'name', order: 'asc' }]"
        @click:row="
          (_event, data) => {
            navigateTo(data.item.id);
          }
        "
      >
        <template #header.name="headerProps">
          <OverviewTableHeader v-bind="headerProps">
            {{ $t('Name') }}
            <span class="font-weight-medium">| {{ $t('ID') }}</span>
          </OverviewTableHeader>
        </template>
        <template #header.category="headerProps">
          <OverviewTableHeader v-bind="headerProps">
            {{ $t('Category_one') }}&nbsp;| {{ $t('Type_one') }}
          </OverviewTableHeader>
        </template>
        <template #header.manufacturer="headerProps">
          <OverviewTableHeader v-bind="headerProps">
            {{ $t('Manufacturer') }}
            <br />
            | {{ $t('Model') }}
            <template v-if="subModelAvailable"
              ><br />
              | {{ $t('SubModel') }}</template
            >
          </OverviewTableHeader>
        </template>
        <template #item.image="{ item }">
          <BaseImage
            v-bind="overviewImageProps"
            :pictureId="item.imageId"
            data-testid="medicalSystemEntityCellImage"
          />
        </template>

        <template #[`item.name`]="{ item }">
          <IdentifierLabel :identifier="item.iuid" :label="item.name" />
          <CommentCount :value="item" @click.stop="navigateTo(item.id, `#${TAB_PREFIX}comments`)" />
        </template>
        <template #[`item.model`]="{ item }">
          {{ $t(`RtSystemModelEnum.${item.model}` as I18nKey) }}
        </template>

        <template #item.state="{ item }">
          {{ $t(`StateOfUseEnum.${item.state}` as I18nKey) }}
          <EntityChange
            :entityChange="getEntityChangeEvent(item, 'editing')"
            :showEmptyFieldSign="false"
          />
        </template>

        <template #item.category="{ item }">
          {{ $t(`RtSystemCategoryEnum.${item.category}` as I18nKey) }}&nbsp;|
          {{ $t(`MedicalSystemSubCategoryEnum.${item.subCategory}` as I18nKey) }}
        </template>
        <template #item.manufacturer="{ item }">
          {{ $t(`RtSystemManufacturerEnum.${item.manufacturer}` as I18nKey) }}<br />|{{
            $t(`RtSystemModelEnum.${item.model}` as I18nKey)
          }}
          <template v-if="item.subModel">
            <br />
            |
            {{ $t(`MedicalSystemSubModelEnum.${item.subModel}` as I18nKey) }}</template
          >
        </template>

        <template #[`item.lastModificationTime`]="{ item }">
          <EntityChange :entityChange="getEntityChangeEvent(item, 'modified')" />
        </template>

        <template #item.contextMenu="{ item }">
          <ContextMenu
            :actions="getContextMenuActions(item)"
            :closeOnScrollElements="tableScrollElement"
          />
        </template>
      </OverviewTable>
    </template>
    <MedicalSystemAddDialog v-model="isAddDialogOpen" @submit="add" />
  </BasePage>
</template>
